/**
 * Pure access rules for the Pipeline Tracker. No imports, deliberately.
 *
 * These live apart from the hook because the hook reaches Firebase at module
 * load, so anything importing it drags a live client into the test runner. The
 * same reasoning keeps the tracker's taxonomy callable out of its barrel.
 */

export type TrackerRollout = 'off' | 'observe' | 'enabled';

export interface PipelineTrackerAccess {
  /** Commercial grant: has this tenant bought it. */
  entitled: boolean;
  rollout: TrackerRollout;
  /** Menu entry visible. Requires the grant AND 'enabled' specifically. */
  showInNav: boolean;
  /** Route resolves. 'observe' allows direct-URL access for founder validation. */
  canOpen: boolean;
  loading: boolean;
}

/**
 * Fail closed on anything unrecognised. A typo in a Firestore flag must never
 * enable a paid feature, and the tri-state convention in this codebase is that
 * only the exact literal counts. `true` is the tempting one: it reads like
 * "on" and means nothing here.
 */
export function parseRollout(raw: unknown): TrackerRollout {
  return raw === 'enabled' ? 'enabled' : raw === 'observe' ? 'observe' : 'off';
}

/**
 * Entitlement and rollout are separate and both must hold. They answer
 * different questions: has this tenant bought it, and is it safe to show them
 * yet. 'observe' keeps the route reachable while the menu stays hidden, so a
 * live-data soak is possible without a tenant seeing a half-checked number.
 */
export function resolveAccess(
  entitled: boolean,
  rollout: TrackerRollout,
  loading: boolean
): PipelineTrackerAccess {
  return {
    entitled,
    rollout,
    showInNav: entitled && rollout === 'enabled',
    canOpen: entitled && rollout !== 'off',
    loading,
  };
}
